// Every address on the generic eval surface, derived from one base.
//
// §4.13.7 publishes a route grammar, binding on every eval surface, opted into
// by declaring a single base:
//
//   /eval                                    the index
//   /eval/suite/<suite>                      the four steps
//   /eval/suite/<suite>/case/<case_id>       read one case
//   /eval/suite/<suite>/label                label: hand over the next case
//   /eval/suite/<suite>/label/<case_id>      label a specific one
//   /eval/suite/<suite>/labeled              re-check what is already labeled
//   /eval/suite/<suite>/labeled/<case_id>    re-check a specific one
//   /eval/dataset/<dataset>[/item/<item_id>]
//
// The point of publishing it is that a link is DERIVED rather than declared:
// a page that names a case can link it without anyone deciding, per page, what
// that link is. Before this file every caller built its own string, three of
// the five surfaces could not produce a link to what they were showing at all,
// and the one that could put its selection in a query string.
//
// Two properties that are easy to lose and expensive to get back:
//
//   - **No state in the query string, and no batch id anywhere.** `?case=` was
//     not an address even when it was written back, and a draw is not a thing
//     a person handles (§4.13.1a) — so labelling is addressed by the suite, and
//     the surface resolves which draw that means.
//   - **`label` and `labeled` are two addresses, not one page with a mode.**
//     `label` hands over the draw — the undecided sample the rates are computed
//     over. `labeled` hands over the audit — every case that already carries a
//     verdict, for re-checking. Making the second a mode of the first would put
//     state back in the query string, and it would leave "re-check labeled
//     cases" with nowhere to link to: that is exactly how a queue whose corpus
//     was fully decided came to render "there is nothing left to draw" with all
//     of its labels behind no address at all.
//   - **Ids carry slashes.** `rec_video_grades/g3_ela_poetry_context/lOUnhSMGdOo`
//     is ONE id with two of them; percent-encoding those addresses a case that
//     does not exist, and encoding nothing breaks on every other character. So
//     each segment is encoded and the slashes are kept.
//
// `EVAL_BASE_URL` mirrors `spec.EVAL_BASE_URL`, which the API reports on its
// index as `eval_base_url` — the two ends of one declaration.

export const EVAL_BASE_URL = '/_eval/all';

// One id, encoded segment by segment.
export function seg(value) {
    return String(value).split('/').map(encodeURIComponent).join('/');
}

export const evalPath = {
    index: () => EVAL_BASE_URL,
    suite: (suite) => `${EVAL_BASE_URL}/suite/${encodeURIComponent(suite)}`,
    case: (suite, caseId) => `${evalPath.suite(suite)}/case/${seg(caseId)}`,
    label: (suite, caseId) => (caseId
        ? `${evalPath.suite(suite)}/label/${seg(caseId)}`
        : `${evalPath.suite(suite)}/label`),
    labeled: (suite, caseId) => (caseId
        ? `${evalPath.suite(suite)}/labeled/${seg(caseId)}`
        : `${evalPath.suite(suite)}/labeled`),
    dataset: (dataset) => `${EVAL_BASE_URL}/dataset/${encodeURIComponent(dataset)}`,
    item: (dataset, itemId) => `${evalPath.dataset(dataset)}/item/${seg(itemId)}`,
};

export default evalPath;
